import { LeadSourceType } from '@mortgage-pos/types'

// For new sources, add a new object to the map where the the key is the "LeadSource" string
// being passed alongside a "LeadSourceId"
// - name: this is the string that will be stored in the "source" column on the Applications table
// - trackingField (optional): this is the name of the property to store the Id value as in Application_Tracking
// - sourceField (optional): legacy BankRate request handler field name to find in request data
type LeadSourceMap = {
  [s in LeadSourceType]: LeadSource
}

export type LeadSource = {
  name: string
  trackingField?: string
  sourceField?: string
  urlMatchString?: string
  urlSecondaryMatchString?: string
  urlMatchParams?: string[]
  isBuilder?: boolean
}

export const leadSourceMap: Partial<LeadSourceMap> = {
  ALFALFA: {
    name: 'Alfalfa',
  },
  AMAZON: {
    name: 'Amazon',
    urlMatchString: 'src=2f49875e',
  },
  AMAZONBENEFITSDEMO: {
    name: 'AmazonBenefitsDemo',
  },
  BANKRATE: {
    name: 'Bankrate',
    trackingField: 'bankrateLeadId',
    sourceField: 'bankrateLeadId',
  },
  BANKRATEDIGIFLEX: {
    name: 'BankrateDigiFlex',
    trackingField: 'bankrateLeadId',
    sourceField: 'bankrateLeadId',
  },
  BANKRATECPC: {
    name: 'BankrateCPC',
    urlMatchString: 'cpc=true',
  },
  BANKRATEEMAIL: {
    name: 'BankrateEmail',
    sourceField: 'bankrateLeadId',
    trackingField: 'bankrateLeadId',
    urlMatchString: 'source=BankrateEmail',
  },
  BANKRATEPRIMARY: {
    name: 'BankratePrimary',
    sourceField: 'bankrateLeadId',
    trackingField: 'bankrateLeadId',
  },
  BANKRATESECONDARY: {
    name: 'BankrateSecondary',
    sourceField: 'bankrateLeadId',
    trackingField: 'bankrateLeadId',
  },
  BANKRATEALFIE: {
    name: 'BankrateAlfie',
  },
  BANKRATEFEDEMAIL: {
    name: 'BankrateFedEmail',
    urlMatchString: 'utm_campaign=ed_ho_bn_fed_pre',
  },
  BANKRATECASHOUTEMAIL: {
    name: 'BankrateCashOutEmail',
    urlMatchString: 'src=br439',
  },
  BACKFILL: {
    name: 'Backfill',
  },
  BUILDERREFERRAL: {
    name: 'BuilderReferral',
  },
  BING: {
    name: 'Bing',
    urlMatchString: 'utm_source=bing',
  },
  CALENDLYAPPOINTMENT: {
    name: 'CalendlyAppointment',
  },
  CONSUMERAFFAIRS: {
    name: 'ConsumerAffairs',
    urlMatchString: 'source=consumeraffairs',
  },
  CPC: {
    name: 'CPC',
    urlMatchString: 'click_id=',
  },
  CUSTOMERREFERRAL: {
    name: 'CustomerReferral',
  },
  EMPLOYEELOANS: {
    name: 'EmployeeLoans',
    urlMatchString: 'src=employee',
  },
  EXISTINGCUSTOMERREFERRAL: {
    name: 'ExistingCustomerReferral',
  },
  FACEBOOK: {
    name: 'Facebook',
    trackingField: 'fbclid',
    sourceField: 'fBLeadId',
  },
  FACET: {
    name: 'Facet',
    urlMatchString: 'facet',
  },
  FED: {
    name: 'Fed',
    urlMatchString: 'src=6rlkye84',
  },
  FREERATEUPDATE: {
    name: 'FreeRateUpdate',
    trackingField: 'freerateupdateLeadId',
  },
  HARRISTEETER: {
    name: 'HarrisTeeter',
    urlMatchString: 'src=harristeeter',
  },
  HOMEEQUITYCPC: {
    name: 'HomeEquityCPC',
    sourceField: 'bankrateLeadId',
    trackingField: 'bankrateLeadId',
    urlMatchString: 'source=bankratehomeequity',
  },
  HOMEEQUITYPRIMARY: {
    name: 'HomeEquityPrimary',
    sourceField: 'bankrateLeadId',
    trackingField: 'bankrateLeadId',
    urlMatchString: 'pid=br_he_rt_co',
  },
  HOMEEQUITYSECONDARY: {
    name: 'HomeEquitySecondary',
    sourceField: 'bankrateLeadId',
    trackingField: 'bankrateLeadId',
    urlMatchString: 'source=bruno:home-equity:quote',
  },
  HOMELIGHT: {
    name: 'HomeLight',
  },
  HSH: {
    name: 'HSH',
    urlMatchString: 'source=HSH',
  },
  HRM: {
    name: 'HRM',
    urlMatchString: 'src=hrm',
    isBuilder: true,
  },
  HRMINCENTIVE: {
    name: 'HRMIncentive',
    urlMatchString: 'src=hrmincentive',
    isBuilder: true,
  },
  INBOUNDCALL: {
    name: 'InboundCall',
  },
  PROPERTYMATE: {
    name: 'PropertyMate',
  },
  PURCHASEREFERRALS: {
    name: 'PurchaseReferrals',
  },
  LENDINGTREE: {
    name: 'LendingTree',
  },
  LOANESTIMATECOMPARISON: {
    name: 'LoanEstimateComparison',
  },
  LOWERMYBILLS: {
    name: 'LowerMyBills',
    urlMatchString: 'source=LowerMyBills',
  },
  LOWERMYBILLSSHORTFORM: {
    name: 'LowerMyBillsShortForm',
  },
  MYAGENTFINDER: {
    name: 'MyAgentFinder',
    urlMatchString: 'source=MyAgentFinder',
  },
  MILITARYVALOAN: {
    name: 'MilitaryVALoan',
    urlMatchParams: ['click_id=', 'prop_id=', 'prop_id=49937'],
  },
  NERDWALLET: {
    name: 'NerdWallet',
    urlMatchString: 'nerdwallet',
  },
  PERSONALREFERRAL: {
    name: 'PersonalReferral',
  },
  RATEZIP: {
    name: 'RateZip',
    urlMatchParams: ['click_id=', 'prop_id=', 'prop_id=48731'],
  },
  REALTORREFERRAL: {
    name: 'RealtorReferral',
  },
  RETCLIENTFORLIFEOB: {
    name: 'RetClientForLifeOB',
  },
  REFILY: {
    name: 'Refily',
  },
  REPORTING: {
    name: 'Reporting',
  },
  ROBINHOOD: {
    name: 'Robinhood',
    urlMatchString: 'src=robinhood',
  },
  RVMN: {
    name: 'RVMN',
    urlMatchParams: ['click_id=', 'prop_id='],
  },
  SAGEMORTGAGEEMAIL: {
    name: 'SageMortgageEmail',
  },
  SAGERADIO: {
    name: 'Sage Radio',
  },
  SAGEWEBSITE: {
    name: 'Sage Website',
  },
  SEM: {
    name: 'SEM',
    trackingField: 'gclid',
    sourceField: 'gclid',
    urlMatchString: 'utm_source=google',
  },
  SIMS: {
    name: 'Sims',
    urlMatchString: 'src=sims',
    isBuilder: true,
  },
  SMARTASSET: {
    name: 'SmartAsset',
    urlMatchString: 'sa_mortgage',
  },
  SUPPORTPHONENUMBER: {
    name: 'SupportPhoneNumber',
  },
  THEMORTGAGEREPORT: {
    name: 'TheMortgageReport',
    urlMatchParams: ['click_id=', 'prop_id=', 'prop_id=49492'],
  },
  TOP10: {
    name: 'Top10',
    urlMatchString: 'source=top10',
  },
  TRANSUNION: {
    name: 'TransUnion',
    trackingField: 'tuLeadOfferCode',
  },
  UNMAPPED: {
    name: 'Unmapped',
    trackingField: 'unmappedLeadId',
  },
  WHITETREE: {
    name: 'WhiteTree',
    urlMatchString: 'src=whitetree',
    isBuilder: true,
  },
  WHITETREEINCENTIVE: {
    name: 'WhiteTreeIncentive',
    urlMatchString: 'src=whitetreeincentive',
    isBuilder: true,
  },
  ZILLOW: {
    name: 'Zillow',
    trackingField: 'zillowLeadId',
  },
}
